/**
 * NavUser — sidebar user footer (issue #7).
 *
 * Adapted from standalone-search's NavUser.tsx.
 * Key changes from the donor:
 *   - Replaced `useScholarAuth` with `useAuth` (LMS AuthContext).
 *   - Removed `useHoldSidebarOpen` — not exported by the LMS sidebar.tsx.
 *   - Removed the theme-preset picker — tenant-driven, never user-choice (#7).
 *   - Removed next-themes `useTheme` — no dark/light toggle in this milestone.
 *   - The person is a neutral glyph, never initials (ADR 0034): a login is
 *     not a person, and initials guessed from a name would claim one.
 *   - Identity comes from `me.full_name` / `me.email` per AuthContext (#5).
 *
 * The account menu is the kit's popover shape: a title row naming the person,
 * rule-separated groups of destinations, and the one destructive act, sign
 * out, last.
 */
import { useState } from "react";
import { useNavigate } from "react-router";
import { CircleUser, EllipsisVertical, GraduationCap, LogOut, School } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useAuth } from "@/contexts/AuthContext";

/** The footer person: a neutral glyph in a chip circle. */
function PersonGlyph() {
  return (
    <span
      aria-hidden="true"
      data-person-glyph
      className="flex size-8 shrink-0 items-center justify-center rounded-full bg-well text-muted-foreground"
    >
      <CircleUser className="size-5" strokeWidth={1.75} />
    </span>
  );
}

export function NavUser() {
  const { isMobile } = useSidebar();
  const { me, signOut } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  const name = me?.full_name ?? me?.email ?? "User";
  const email = me?.email ?? "";
  const hasLearningWorkspace = me?.workspaces?.learning?.available === true;
  const hasTeachingDirectory = me?.workspaces?.teaching?.available === true;
  const facultyDirectoryPath = hasLearningWorkspace
    ? "/student/faculty"
    : me?.role === "admin"
      ? "/admin/people?relationship=faculty"
      : "/instructor/faculty";
  const roleLabel = me?.role ? `${me.role[0].toUpperCase()}${me.role.slice(1)}` : "Member";

  const handleLogout = async () => {
    await signOut();
    void navigate("/sign-in", { replace: true });
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              aria-label="Open account menu"
              title="Open account menu"
              className="transition-colors duration-[var(--motion-fast)] ease-[var(--ease-standard)] data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <PersonGlyph />
              <span className="grid min-w-0 flex-1 text-left text-ui">
                <span className="truncate font-medium">{name}</span>
                <span className="truncate text-meta text-muted-foreground">{roleLabel}{email ? ` · ${email}` : ""}</span>
              </span>
              <EllipsisVertical
                className="ms-auto size-4 shrink-0 text-muted-foreground"
                strokeWidth={1.75}
                aria-hidden="true"
              />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-2 py-1.5 text-left text-ui">
                <PersonGlyph />
                <span className="grid min-w-0 flex-1 text-left">
                  <span className="truncate font-medium">{name}</span>
                  <span className="truncate text-meta text-muted-foreground">{email}</span>
                </span>
              </div>
            </DropdownMenuLabel>
            {(hasLearningWorkspace || hasTeachingDirectory) && (
              <>
                <DropdownMenuSeparator />
                {hasLearningWorkspace && (
                  <>
                    <DropdownMenuItem onClick={() => void navigate("/student/profile")}>
                      <CircleUser className="size-4" strokeWidth={1.75} aria-hidden="true" />
                      Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => void navigate("/student/grades")}>
                      <GraduationCap className="size-4" strokeWidth={1.75} aria-hidden="true" />
                      Academic record
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuItem
                  onClick={() => void navigate(facultyDirectoryPath)}
                >
                  <School className="size-4" strokeWidth={1.75} aria-hidden="true" />
                  Faculty directory
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem variant="destructive" onClick={() => void handleLogout()}>
              <LogOut className="size-4" strokeWidth={1.75} aria-hidden="true" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
